import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { useDebounced } from './useDebounced.js';

export interface AudienceRef {
  kind: 'list' | 'segment';
  id: string;
}

export interface AudienceBreakdown {
  roh: number;
  mehrfach: number;
  brutto: number;
  ausgeschlossen: number;
  geloescht: number;
  abgemeldet: number;
  unzustellbar: number;
  beschwert: number;
  gesperrt: number;
  netto: number;
}

export interface CampaignSummary {
  id: string;
  name: string;
  type: string;
  status: string;
  fromName: string;
  fromEmail: string;
  replyTo?: string | null;
  preheader?: string | null;
  sendingPolicyId?: string | null;
  startedAt: string | null;
  completedAt: string | null;
  scheduledAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  recipients?: number;
  sent?: number;
  uniqueOpens?: number;
  uniqueClicks?: number;
  openRate?: number | null;
  clickRate?: number | null;
  stats?: {
    total: number;
    sent: number;
    opens: number;
    clicks: number;
    replies: number;
  };
}

/** Warum gerade nichts rausgeht. grund = null heisst: es läuft. */
export interface Versandlage {
  grund: string | null;
  abhilfe: string | null;
  aktiveAbsender: number;
  faellig: number;
  spaeter: number;
  zurueckgehalten: number;
  naechsteFaelligkeit: string | null;
  letzterVersand: string | null;
}

export interface CampaignStats {
  total: number;
  queued: number;
  sending: number;
  sent: number;
  failed: number;
  skipped: number;
  uniqueOpens: number;
  uniqueClicks: number;
  machineOpens: number;
  unsubscribes: number;
  bounces: number;
  openRate: number | null;
  clickRate: number | null;
  skipReasons: Record<string, number>;
  linkStats?: Array<{ url: string; totalClicks: number; uniqueClicks: number }>;
}

export interface CampaignVariant {
  id: string;
  key: string;
  subject: string;
  html: string;
  weight: number;
  isWinner: boolean;
}

export const campaignsQuery = queryOptions({
  queryKey: ['campaigns'],
  queryFn: ({ signal }) => api.get<{ items: CampaignSummary[] }>('/api/campaigns', signal),
  staleTime: 10_000,
});

export interface CampaignAudience {
  include: Array<{ kind: 'list' | 'segment'; id: string }>;
  exclude: Array<{ kind: 'list' | 'segment'; id: string }>;
}

/** Die Einzelkampagne traegt die Felder, die zum Bearbeiten noetig sind. */
export interface CampaignDetail extends CampaignSummary {
  audience: CampaignAudience;
  replyTo: string | null;
  preheader: string | null;
  sendingPolicyId: string | null;
}

export const campaignQuery = (id: string) =>
  queryOptions({
    queryKey: ['campaigns', id],
    queryFn: ({ signal }) =>
      api.get<{
        campaign: CampaignDetail;
        variants: CampaignVariant[];
        stats: CampaignStats;
        versandlage: Versandlage;
      }>(`/api/campaigns/${id}`, signal),
    // Waehrend des Versands aendern sich die Zahlen laufend. Geplante
    // Kampagnen ebenso: dort laeuft die Uhr bis zum Versandfenster.
    refetchInterval: (query) =>
      query.state.data?.campaign.status === 'sending' ||
      query.state.data?.campaign.status === 'scheduled'
        ? 5_000
        : false,
  });

/** Migration 0029 — ersetzt sendDays/sendFrom/sendTo. */
export interface SendWindow {
  tage: number[];
  von: string;
  bis: string;
}

export interface SendingPolicy {
  id: string;
  name: string;
  emailsPerHour: number;
  emailsPerMinute: number;
  emailsPerDay: number;
  warmupEnabled: boolean;
  warmupStart: number;
  warmupMultiplier: number;
  warmupCap: number;
  sendWindows: SendWindow[];
  blackoutDates: string[];
  contactCooldownMinutes: number;
  companyCooldownMinutes: number;
  maxContactPerDay: number;
  maxCompanyPerDay: number;
  maxDomainPerHour: number;
  timezone: string;
  providerOrder: string[];
}

export const sendingPoliciesQuery = queryOptions({
  queryKey: ['sending-policies'],
  queryFn: ({ signal }) => api.get<{ items: SendingPolicy[] }>('/api/sending-policies', signal),
  staleTime: 60_000,
});

export interface CreateCampaignInput {
  name: string;
  fromName: string;
  fromEmail: string;
  replyTo?: string | null;
  preheader?: string | null;
  sendingPolicyId?: string | null;
  deliverAtLocalHour?: number | null;
  scheduledAt?: string | null;
  audience: {
    include: AudienceRef[];
    exclude: AudienceRef[];
    enforceManual?: boolean;
    enforceUnsubscribe?: boolean;
    enforceNoInterest?: boolean;
  };
  variants: Array<{ key: string; subject: string; html: string; weight: number }>;
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      api.post<{ campaign: CampaignSummary }>('/api/campaigns', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaign(id?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id: inputId, ...input }: Partial<UpdateCampaignInput> & { id?: string }) => {
      const targetId = inputId ?? id;
      if (!targetId) throw new Error('Keine Kampagnen-ID übergeben.');
      return api.patch<{ campaign: CampaignSummary }>(`/api/campaigns/${targetId}`, input);
    },
    onSuccess: (_data, variables) => {
      const targetId = variables.id ?? id;
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      if (targetId) void queryClient.invalidateQueries({ queryKey: ['campaigns', targetId] });
    },
  });
}

export function useCampaignAction(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: 'start' | 'pause' | 'resume' | 'cancel') =>
      api.post<{ queued?: number; suppressed?: number; testCohort?: number }>(
        `/api/campaigns/${id}/${action}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function useScheduleCampaign(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduledAt: string) =>
      api.post<{ queued: number; suppressed: number; scheduledAt: string }>(
        `/api/campaigns/${id}/schedule`,
        { scheduledAt },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export interface UpdateCampaignInput {
  name?: string;
  fromName?: string;
  fromEmail?: string;
  replyTo?: string | null;
  preheader?: string | null;
  sendingPolicyId?: string | null;
  audience?: {
    include: Array<{ kind: 'list' | 'segment'; id: string }>;
    exclude: Array<{ kind: 'list' | 'segment'; id: string }>;
  };
  variants?: Array<{ key: string; subject: string; html: string; weight?: number }>;
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ campaign: CampaignSummary }>(`/api/campaigns/${id}/duplicate`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/campaigns/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useAudiencePreview(id: string) {
  return useMutation({
    mutationFn: () =>
      api.post<{ recipients: number; suppressed: number }>(`/api/campaigns/${id}/audience-preview`),
  });
}

export interface CampaignRecipient {
  messageId: string;
  contactId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  skipReason: string | null;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
}

/*
 * Bezeichnung und Farbton des Zustands stehen an einer Stelle, weil Liste und
 * Detailansicht nebeneinander sichtbar sind. Zwei Uebersetzungstabellen fuer
 * dasselbe Wort laufen sonst auseinander — im Altsystem hiess dieselbe
 * Kampagne in der Liste "Wird versendet" und daneben "Läuft".
 */
export const CAMPAIGN_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  scheduled: 'Geplant',
  sending: 'Wird versendet',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
};

export type CampaignTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export function campaignStatusTone(status: string): CampaignTone {
  switch (status) {
    case 'sending':
      return 'accent';
    case 'completed':
      return 'success';
    case 'scheduled':
    case 'paused':
      return 'warning';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function campaignStatusLabel(status: string): string {
  return CAMPAIGN_STATUS_LABELS[status] ?? status;
}

/** Anteil als Prozenttext. `null` bleibt „—" — 0 % waere eine andere Aussage. */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1).replace('.', ',')} %`;
}

export function useAutoResendCampaign(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { newSubject: string }) => {
      const newSubject = typeof input === 'string' ? input : input.newSubject;
      return api.post<{ campaign: CampaignSummary; snapshotCount: number }>(
        `/api/campaigns/${id}/auto-resend`,
        { newSubject },
      );
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export async function scheduleCampaignById(id: string, scheduledAt: string) {
  return api.post<{ queued: number; suppressed: number; scheduledAt: string }>(
    `/api/campaigns/${id}/schedule`,
    { scheduledAt },
  );
}

export async function startCampaignById(id: string) {
  return api.post<{ queued?: number; suppressed?: number }>(`/api/campaigns/${id}/start`);
}

export interface EmpfaengerVorschauResponse {
  grossCount: number;
  excludedCount: number;
  suppressedCount: number;
  netCount: number;
  aufschluesselung: AudienceBreakdown;
}

export function useEmpfaengerVorschau(audience: {
  include: AudienceRef[];
  exclude: AudienceRef[];
  enforceManual?: boolean;
  enforceUnsubscribe?: boolean;
  enforceNoInterest?: boolean;
  excludeHasWonDeal?: boolean;
}) {
  const debouncedAudience = useDebounced(audience, 300);
  const key = JSON.stringify(debouncedAudience);
  const query = useQuery({
    queryKey: ['audience-preview', key],
    queryFn: ({ signal }) =>
      api.post<EmpfaengerVorschauResponse>(
        '/api/campaigns/preview-audience',
        { audience: debouncedAudience },
        signal,
      ),
    enabled: debouncedAudience.include.length > 0,
    staleTime: 5_000,
  });

  return {
    ...query,
    netto: query.data?.netCount ?? 0,
    vorschau: query.data,
    laeuft: query.isLoading,
    fehler: query.error ? (query.error instanceof Error ? query.error.message : 'Vorschau fehlgeschlagen') : null,
  };
}

export function testSendsQuery(id: string) {
  return queryOptions({
    queryKey: ['test-sends', id],
    queryFn: ({ signal }) =>
      api.get<{
        items: Array<{
          id: string;
          recipients: string[];
          ok: boolean;
          error: string | null;
          createdAt: string;
        }>;
      }>(`/api/campaigns/${id}/test-sends`, signal),
    enabled: Boolean(id),
  });
}

export function useTestSend(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { empfaenger: string[]; kontaktId?: string; variantKey?: string }) =>
      api.post<{
        ok: boolean;
        gesendet?: number;
        verwendeterKontakt?: { id: string; name: string; email?: string } | null;
        fehler?: Array<{ empfaenger: string; grund: string }>;
      }>(`/api/campaigns/${id}/test-send`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['test-sends', id] });
    },
  });
}

export function campaignRecipientsQuery(
  id: string,
  options: { filter?: string; search?: string; cursor?: string } = {},
) {
  const query = new URLSearchParams();
  if (options.filter) query.set('filter', options.filter);
  if (options.search) query.set('search', options.search);
  if (options.cursor) query.set('cursor', options.cursor);
  const queryStr = query.toString();

  return queryOptions({
    queryKey: ['campaign-recipients', id, options.filter, options.search, options.cursor],
    queryFn: ({ signal }) =>
      api.get<{
        items: Array<{
          messageId: string;
          contactId: string;
          email: string;
          firstName: string | null;
          lastName: string | null;
          contactStatus?: string;
          isSuppressed?: boolean;
          status: string;
          skipReason: string | null;
          errorMessage: string | null;
          scheduledFor: string | null;
          sentAt: string | null;
          openedAt: string | null;
          clickedAt: string | null;
          bouncedAt: string | null;
          dealStatus?: string;
          dealTitle?: string | null;
        }>;
        nextCursor: string | null;
      }>(`/api/campaigns/${id}/recipients${queryStr ? `?${queryStr}` : ''}`, signal),
  });
}

export function describeWindows(windows: SendWindow[]): string {
  if (!windows || windows.length === 0) return 'Niemals (kein Sendefenster gewählt)';
  const daysMap: Record<number, string> = {
    0: 'So',
    1: 'Mo',
    2: 'Di',
    3: 'Mi',
    4: 'Do',
    5: 'Fr',
    6: 'Sa',
  };
  return windows
    .map((w) => {
      const tageStr = w.tage.map((d) => daysMap[d] ?? d).join(',');
      const zeitStr = w.von === w.bis ? 'ganzer Tag' : `${w.von}–${w.bis}`;
      return `${tageStr} ${zeitStr}`;
    })
    .join(' · ');
}
